import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

interface NavItem {
  label: string;
  to: string;
  icon: JSX.Element;
  roles?: Array<"super_admin" | "moderator">;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function Icon(path: string) {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 transition-transform duration-150" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

// Round 33 point 10 — reorganized again to follow the client's requested
// top-level grouping (Users / Rooms / Economy / Content) rather than the
// finer-grained Round 30 split, while keeping every existing route/icon/
// role-gate exactly as it was. "Bans" and "Room Bans" in the client's
// example aren't separate nav entries here because there's no dedicated
// ban-list page/route behind them — ban/unban/history is an action set on
// the existing Users / User Detail and Rooms pages (see UserDetailPage.tsx,
// RoomsPage.tsx), not a standalone screen, so nothing was added to nav for
// it. Diamond Adjustments (point 8) is the one genuinely new page, slotted
// into Economy next to its coin equivalent. Verification Requests and
// Rankings move under Users per the requested grouping; Room Themes/
// Reactions/Clubs stay under Rooms since they're room-scoped features with
// nowhere else sensible to go. Chat Moderation (1:1 DM investigation, not
// Chat Hall — that feature was fully removed in migration 0067 and this
// page never touched it) and Reports move into a small Moderation group.
// Nothing here changes what any page does — purely navigation/organization.
const RAW_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", to: "/", icon: Icon("M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6") }],
  },
  {
    label: "Users",
    items: [
      { label: "Users", to: "/users", icon: Icon("M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-4a4 4 0 10-4-4 4 4 0 004 4zm6 4a4 4 0 10-4-4 4 4 0 004 4z") },
      // Round 39 item 10 — "Verification Requests" removed entirely
      // (the Request Verification feature and its table are gone; see
      // migration 0096). Direct admin verify/unverify stays on the
      // Users / User Detail pages, untouched.
      { label: "Rankings", to: "/rankings", icon: Icon("M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14") },
      // Round 48 item 6 — moved into the Users group. Round 30 — assigns a custom public ID + badge level (1-7) on top
      // of a user's original id, without ever touching the original id or
      // the internal Auth UUID (see routes/admin/unique-ids.js).
      { label: "Unique ID Management", to: "/unique-ids", icon: Icon("M15 7a2 2 0 012 2m4 0a6 6 0 11-12 0 6 6 0 0112 0zM3 21l3.5-3.5"), roles: ["super_admin"] },
    ],
  },
  {
    label: "Rooms",
    items: [
      { label: "Voice Rooms", to: "/rooms", icon: Icon("M19 11a7 7 0 01-14 0m7 7v4m-4 0h8M8 11V5a4 4 0 118 0v6a4 4 0 01-8 0z") },
      { label: "Room Themes", to: "/room-themes", icon: Icon("M4 6a2 2 0 012-2h8l6 6v10a2 2 0 01-2 2H6a2 2 0 01-2-2V6z M14 4v6h6") },
      { label: "Theme Categories", to: "/room-theme-categories", icon: Icon("M4 5h16M4 10h16M4 15h10M4 20h6") },
      { label: "Room Categories", to: "/room-categories", icon: Icon("M4 6h16M4 12h16M4 18h7") },
      { label: "Room Reactions", to: "/room-reactions", icon: Icon("M12 15a3 3 0 100-6 3 3 0 000 6z M21 12a9 9 0 11-18 0 9 9 0 0118 0z") },
      { label: "Clubs", to: "/clubs", icon: Icon("M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-4a4 4 0 10-4-4 4 4 0 004 4z") },
    ],
  },
  {
    label: "Economy",
    items: [
      { label: "Coin Products", to: "/coin-products", icon: Icon("M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z") },
      { label: "Coin Ledger", to: "/coin-ledger", icon: Icon("M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z") },
      // Round 30 — server-authorized + audited Send/Deduct Coins
      // (routes/admin/coin-adjustments.js), super_admin only — same bar
      // as Send Medal and Unique ID Management, since this one directly
      // moves real balance.
      { label: "Send / Deduct Coins", to: "/coin-adjustments", icon: Icon("M17 9V7a4 4 0 00-8 0v2M5 9h14l1 12H4L5 9z"), roles: ["super_admin"] },
      // Round 50 — Forbidden Words (server-side masking) and Post Review
      // (approval setting + pending queue).
      { label: "Forbidden Words", to: "/forbidden-words", icon: Icon("M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"), roles: ["super_admin", "moderator"] },
      { label: "Post Review", to: "/post-review", icon: Icon("M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z") },
      // Round 49 — Room Level EXP table and Charm/Wealth/Room EXP adjust/reset
      // (routes/admin/exp.js), super_admin only.
      { label: "Room Level EXP", to: "/room-levels", icon: Icon("M3 17l6-6 4 4 8-8M14 7h7v7"), roles: ["super_admin"] },
      { label: "EXP Adjustments", to: "/exp-adjustments", icon: Icon("M12 6v6m0 0v6m0-6h6m-6 0H6"), roles: ["super_admin"] },
      // Round 33 point 8 — the diamond mirror of Send/Deduct Coins
      // (routes/admin/diamonds.js), same super_admin bar, same reasoning.
      { label: "Send / Deduct Diamonds", to: "/diamond-adjustments", icon: Icon("M6 3h12l3 5-9 13L3 8l3-5z M3 8h18M12 3v18"), roles: ["super_admin"] },
      { label: "Gifts", to: "/gifts", icon: Icon("M20 12v10H4V12M2 7h20v5H2V7zm10-5v20M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z") },
      { label: "VIP Products", to: "/vip-products", icon: Icon("M5 3l1.5 4.5h11L19 3M4 8h16l-1.5 12h-13L4 8zM9 12v4m6-4v4") },
      { label: "Purchases", to: "/purchases", icon: Icon("M9 7h6m0 10v-3m-3 3v-3m-3 3v-3m10-6h1a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1h1M9 7V5a3 3 0 016 0v2M9 7h6") },
      { label: "Refunds", to: "/refunds", icon: Icon("M3 10h11a5 5 0 010 10H8m-5-10l4-4m-4 4l4 4") },
      { label: "Rewarded Ad Credits", to: "/rewards", icon: Icon("M15 10l4.55-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.45.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z") },
      { label: "Withdrawals", to: "/withdrawals", icon: Icon("M17 9V7a4 4 0 00-8 0v2M5 9h14l1 12H4L5 9z") },
      // Round 30 — Diamond withdrawal review (routes/admin/diamond-withdrawals.js)
      // and the host-eligibility allowlist that gates who can even submit
      // one (routes/admin/withdrawal-eligibility.js). Eligibility is
      // super_admin only — same bar as Send/Deduct Coins, since it
      // controls who can turn diamonds into a real payout.
      { label: "Diamond Withdrawals", to: "/diamond-withdrawals", icon: Icon("M6 3h12l3 5-9 13L3 8l3-5z M3 8h18M12 3v18") },
      { label: "Withdrawal Eligibility", to: "/withdrawal-eligibility", icon: Icon("M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"), roles: ["super_admin"] },
      // Round 21 point 7 — real Admin Panel control for the gift-receiving
      // percentage (never hardcoded, never Android-trusted). Same
      // super_admin gating as App Settings, since this directly controls
      // real money-equivalent economy math.
      { label: "Economy Settings", to: "/economy-settings", icon: Icon("M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"), roles: ["super_admin"] },
      // Round 45 follow-up, item 12 — the configurable system-message
      // sender account used for coin/medal/mall-item/daily-signin reward
      // notifications.
      { label: "System Sender Account", to: "/system-sender-settings", icon: Icon("M3 8l9 6 9-6M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"), roles: ["super_admin"] },
      { label: "Security & Integrity", to: "/security-settings", icon: Icon("M12 3l8 3v6c0 4.5-3.2 8.4-8 9-4.8-.6-8-4.5-8-9V6l8-3z"), roles: ["super_admin"] },
    ],
  },
  {
    label: "Content",
    items: [
      { label: "Posts & Moments", to: "/posts", icon: Icon("M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 9h16M9 21V9") },
      // Round 30 point 2 — the Mall's catalog (Profile > Mall tab in the
      // app). coins_price here is the only source of truth for what an
      // item costs — never hardcoded in Android (routes/coins.js's
      // buy_mall_item RPC).
      { label: "Mall Categories", to: "/mall-categories", icon: Icon("M4 6h16M4 12h16M4 18h7") },
      { label: "Mall Items", to: "/mall-items", icon: Icon("M20 12v10H4V12M2 7h20v5H2V7zm10-5v20M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z") },
      // Round 58 - avatar-opening metadata for frame images (CP frame, avatar frames); super_admin because it changes what every user sees.
      { label: "Frame Editor", to: "/frame-editor", icon: Icon("M4 4h16v16H4V4zm4 4h8v8H8V8z"), roles: ["super_admin"] },
      // Round 45 client item 6 — send a Mall item (frame/car/etc)
      // directly to any user with an expiry, no coin charge
      // (routes/admin/mall-items.js's grant endpoint). Open to any
      // admin, same as the rest of Mall's catalog CRUD — not gated to
      // super_admin the way Send Medal is.
      { label: "Send Mall Item", to: "/send-mall-item", icon: Icon("M12 15a3 3 0 100-6 3 3 0 000 6z M12 15v6l-3-2-3 2v-6") },
      // Round 45 client item 8 — the consecutive-day reward cycle
      // (migration 0106) Android's Daily Sign-in screen reads from. Open
      // to any admin, same access level as the rest of Mall's catalog.
      { label: "Daily Sign-in", to: "/daily-signin", icon: Icon("M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z") },
      // Round 30 point 5 — Medals catalog (Profile > Medals tab). Never
      // purchased — see Send Medal below for actually awarding one.
      { label: "Medal Categories", to: "/medal-categories", icon: Icon("M4 6h16M4 12h16M4 18h7") },
      { label: "Medals", to: "/medals", icon: Icon("M12 15a3 3 0 100-6 3 3 0 000 6z M12 15v6l-3-2-3 2v-6") },
      // Round 30 point 5 — server-authorized + audited Send Medal
      // (routes/admin/send-medal.js), super_admin only.
      { label: "Send Medal", to: "/send-medal", icon: Icon("M12 15a3 3 0 100-6 3 3 0 000 6z M12 15v6l-3-2-3 2v-6"), roles: ["super_admin"] },
      // Round 55 - Weekly CP event management (its own entry, not hidden under Content/Economy).
      { label: "CP Events", to: "/cp-events", icon: Icon("M4.3 6.3a4.5 4.5 0 016.4 0L12 7.6l1.3-1.3a4.5 4.5 0 116.4 6.4L12 20.4l-7.7-7.7a4.5 4.5 0 010-6.4z") },
      // Round 54 - Agency & Host management (mutations are super_admin on the Worker).
      { label: "Agencies", to: "/agencies", icon: Icon("M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6") },
      { label: "Hosts", to: "/hosts", icon: Icon("M12 14a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0") },
      { label: "Agency Audit Logs", to: "/agency-audit", icon: Icon("M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-3-2-2 2-2-2-2 2-2-2-3 2V6a2 2 0 012-2z") },
      { label: "Gift Catalog", to: "/gift-catalog", icon: Icon("M4 7h16v13H4V7zM4 7l2-4h12l2 4M9 11v5m6-5v5") },
      { label: "Gift Categories", to: "/gift-categories", icon: Icon("M4 6h16M4 12h16M4 18h7") },
      // Round 39 points 3/4 — the global/all-room gift banner's tier
      // catalog (min gift value -> banner animation).
      { label: "Gift Broadcast Levels", to: "/gift-broadcast-levels", icon: Icon("M4 7h16v13H4V7zM4 7l2-4h12l2 4M9 11v5m6-5v5") },
    ],
  },
  {
    label: "Moderation",
    items: [
      // Chat Moderation is 1:1 DM investigation over its own messaging
      // table — distinct from Chat Hall, which was fully removed project-
      // wide (migration 0067_round31_remove_chat_hall.sql) and never
      // touched by this page. Still live and backed by routes/admin/
      // chat.js.
      { label: "Chat Moderation", to: "/chat-moderation", icon: Icon("M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z") },
      { label: "Reports", to: "/reports", icon: Icon("M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z") },
      // Round 45 follow-up, item 4 — Profile's Feedback entry
      // (ic_faceback) submissions, migration 0109.
      { label: "Feedback", to: "/feedback", icon: Icon("M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z") },
      // Round 45 follow-up, item 13 — granular per-user permissions
      // (power_admin/host_anywhere/ban), migration 0110. super_admin-only,
      // same bar as granting a Send Medal/Coin Adjustments-style privilege.
      { label: "User Backpack", to: "/user-backpack", icon: Icon("M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"), roles: ["super_admin"] },
      { label: "User Permissions", to: "/user-permissions", icon: Icon("M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v2"), roles: ["super_admin"] },
      // Round 45 follow-up, item 13 — review + revoke Mute/Ban Chat/Ban
      // Account actions issued in-app by 'ban'-permitted users.
      // Round 45G — Force Update (blocking app update dialog + server gate).
      { label: "Splash Screen", to: "/splash-screen", icon: Icon("M4 4h16v16H4V4zm4 4v8h8V8H8z"), roles: ["super_admin"] },
      { label: "Force Update", to: "/force-update", icon: Icon("M12 3l6 7h-4v7h-4v-7H6l6-7zM5 19h14v2H5z"), roles: ["super_admin"] },
      { label: "Moderation Console", to: "/moderation-history", icon: Icon("M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"), roles: ["super_admin"] },
    ],
  },
  {
    label: "Notifications",
    items: [
      { label: "Notifications", to: "/notifications", icon: Icon("M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9") },
      // Round 35 items 7/8/12 — Messages > System Messages, Activity
      // Messages, per the client's explicit admin nav grouping. Two
      // separate pages/routes/tables (see MessagesPages.tsx's own doc) —
      // never merged with the broadcast-only Notifications entry above.
      { label: "System Messages", to: "/system-messages", icon: Icon("M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9") },
      { label: "Activity Messages", to: "/activity-messages", icon: Icon("M13 10V3L4 14h7v7l9-11h-7z") },
      { label: "Banners", to: "/banners", icon: Icon("M4 6a2 2 0 012-2h12a2 2 0 012 2v2H4V6zM4 10h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z") },
    ],
  },
  {
    label: "Growth & Engagement",
    items: [{ label: "Games", to: "/games", icon: Icon("M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z") }],
  },
  {
    label: "Testing Tools",
    items: [
      // Round 38 — real bot accounts for exercising room occupancy without
      // needing human testers (routes/admin/bots.js).
      { label: "Bot Management", to: "/bots", icon: Icon("M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2zM9 9h.01M15 9h.01M9 13h6") },
      { label: "Bot Rooms", to: "/bot-rooms", icon: Icon("M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2zM9 9h.01M15 9h.01M9 13h6") },
    ],
  },
  {
    label: "Settings / Configuration",
    items: [
      { label: "App Settings", to: "/settings", icon: Icon("M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"), roles: ["super_admin"] },
      { label: "Ads Configuration", to: "/ads-config", icon: Icon("M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z") },
      // Round 30 — per-method Google/etc. sign-in ON/OFF, enforced for
      // real in routes/auth.js, not just an app_settings row nobody reads.
      { label: "Login Methods", to: "/login-methods", icon: Icon("M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v2h8z"), roles: ["super_admin"] },
      { label: "Translations", to: "/translations", icon: Icon("M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.5 7a13.6 13.6 0 01-5 8"), roles: ["super_admin"] },
    ],
  },
  {
    label: "Security / Admin",
    items: [
      { label: "Admin Users", to: "/admin-users", icon: Icon("M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"), roles: ["super_admin"] },
      { label: "Audit Log", to: "/audit-log", icon: Icon("M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"), roles: ["super_admin"] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Round 56 (6A) - the sections the admin actually sees. RAW_GROUPS above is the
// untouched inventory of every module (route, icon, role gate). This table only
// decides which section each one is shown in, so nothing can be dropped: any item
// not named below is appended to an "Other" section instead of disappearing.
// ---------------------------------------------------------------------------
const MSG_ICON = "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z";
const EXTRA_ITEMS: NavItem[] = [
  // Round 56 item 4 - Customer Care account (separate from the System Sender)
  { label: "Customer Care", to: "/customer-care", icon: Icon("M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"), roles: ["super_admin"] },
  // Round 56 item 5 - Allow App on Emulators
  { label: "Device Access", to: "/emulator-access", icon: Icon("M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"), roles: ["super_admin"] },
];
void MSG_ICON;

const SECTION_LAYOUT: Array<{ label: string; items: string[] }> = [
  { label: "Overview", items: ["Dashboard"] },
  { label: "Users & Moderation", items: ["Users", "Rankings", "Unique ID Management", "User Permissions", "Moderation Console"] },
  { label: "Messaging", items: ["System Sender Account", "Customer Care", "System Messages", "Activity Messages", "Notifications", "Chat Moderation", "Feedback"] },
  { label: "Coins & Payments", items: ["Coin Products", "Coin Ledger", "Send / Deduct Coins", "Purchases", "Refunds", "Rewarded Ad Credits", "Economy Settings"] },
  { label: "Diamonds & Payouts", items: ["Send / Deduct Diamonds", "Diamond Withdrawals", "Withdrawals", "Withdrawal Eligibility"] },
  { label: "Gifts", items: ["Gifts", "Gift Catalog", "Gift Categories", "Gift Broadcast Levels"] },
  { label: "Mall & Backpack", items: ["Mall Categories", "Mall Items", "Send Mall Item", "User Backpack", "Daily Sign-in"] },
  { label: "VIP, Levels & Medals", items: ["VIP Products", "Medal Categories", "Medals", "Send Medal", "Room Level EXP", "EXP Adjustments"] },
  { label: "CP", items: ["CP Events"] },
  { label: "Agencies & Hosts", items: ["Agencies", "Hosts", "Agency Audit Logs"] },
  { label: "Rooms & Live Features", items: ["Voice Rooms", "Room Themes", "Theme Categories", "Room Categories", "Room Reactions", "Clubs", "Games"] },
  { label: "Content", items: ["Posts & Moments", "Reports", "Post Review", "Forbidden Words", "Banners"] },
  { label: "Security", items: ["Device Access", "Splash Screen", "Force Update", "Security & Integrity", "Login Methods"] },
  { label: "Administration", items: ["Admin Users", "Audit Log", "App Settings", "Ads Configuration", "Translations"] },
  { label: "Testing Tools", items: ["Bot Management", "Bot Rooms"] },
];

function buildNavGroups(): NavGroup[] {
  const byLabel = new Map<string, NavItem>();
  for (const g of RAW_GROUPS) for (const it of g.items) byLabel.set(it.label, it);
  for (const it of EXTRA_ITEMS) byLabel.set(it.label, it);
  const placed = new Set<string>();
  const groups: NavGroup[] = SECTION_LAYOUT.map((sec) => ({
    label: sec.label,
    items: sec.items.flatMap((l) => {
      const it = byLabel.get(l);
      if (!it) return [];
      placed.add(l);
      return [it];
    }),
  }));
  const leftovers = [...byLabel.values()].filter((it) => !placed.has(it.label));
  if (leftovers.length > 0) groups.push({ label: "Other", items: leftovers });
  return groups;
}

const NAV_GROUPS: NavGroup[] = buildNavGroups();

// Groups small enough (or important enough, like Overview) that collapsing
// them would only cost a click for no real benefit — always rendered open,
// with no chevron/toggle at all.
const ALWAYS_OPEN_GROUPS = new Set(["Overview"]);

export function Sidebar({ mobileOpen = false, onClose }: { mobileOpen?: boolean; onClose?: () => void }) {
  const { session } = useAuth();
  const location = useLocation();

  const groupContainingActiveRoute = useMemo(() => {
    for (const group of NAV_GROUPS) {
      if (group.items.some((item) => (item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to)))) {
        return group.label;
      }
    }
    return null;
  }, [location.pathname]);

  // Collapsed by default, except whichever group holds the page you're
  // currently on — so landing on, say, Mall Items opens straight into the
  // "Mall / Items" group instead of it looking empty/collapsed under your
  // own active page. Expanding a group afterward is a plain toggle; it
  // doesn't fight this initial choice again once you've touched it.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(groupContainingActiveRoute ? [groupContainingActiveRoute] : [])
  );

  function toggleGroup(label: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  // Close the drawer after navigating (small screens only; harmless on desktop).
  useEffect(() => {
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={onClose} aria-hidden="true" />}
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-screen w-64 flex-col border-r border-surface-border bg-surface-raised transition-transform duration-200 lg:static lg:translate-x-0 ${
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex items-center gap-2.5 border-b border-surface-border px-5 py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold text-sm font-bold text-surface">4F</div>
        <div>
          <p className="text-sm font-bold text-ink">4Fun Admin</p>
          <p className="text-[11px] text-ink-faint">Control Center</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter((item) => !item.roles || (session && item.roles.includes(session.role)));
          if (visibleItems.length === 0) return null;

          const alwaysOpen = ALWAYS_OPEN_GROUPS.has(group.label);
          const isOpen = alwaysOpen || openGroups.has(group.label);
          const isActiveGroup = group.label === groupContainingActiveRoute;

          return (
            <div key={group.label} className="pb-1">
              {alwaysOpen ? (
                <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-wider text-ink-faint">{group.label}</p>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className={`focus-ring flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    isActiveGroup ? "text-gold" : "text-ink-faint hover:text-ink-muted"
                  }`}
                  aria-expanded={isOpen}
                >
                  <span>{group.label}</span>
                  <span className={isOpen ? "rotate-90" : ""}>
                    <ChevronIcon />
                  </span>
                </button>
              )}

              {isOpen && (
                <div className="space-y-0.5 pt-1">
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `focus-ring flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                          isActive ? "border-l-2 border-gold bg-gold/10 text-gold" : "border-l-2 border-transparent text-ink-muted hover:bg-surface-card hover:text-ink"
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
    </>
  );
}
